import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, addDays, isBefore, differenceInDays } from "date-fns";
import { Plus, Trash2, CalendarIcon } from "lucide-react";
import { useFilteredRequests } from "@/hooks/useFilteredRequests";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  LeavePolicyBalance,
  TimeOffResponse,
} from "@/components/submission/types";
import { Label } from "@/components/ui/label";
import { TimeOffRequest } from "@/types/nanny";

interface LeaveType {
  id: number;
  displayName: string;
}

interface TimeOffSectionProps {
  timeOffRequests: TimeOffRequest[];
  leavePolicies: LeavePolicyBalance[];
  submissionId: number;
  isEditable: boolean;
  onUpdate: (timeOff: TimeOffRequest[]) => void;
}

const timeOffSchema = z
  .object({
    leaveTypeId: z.string().min(1, "Please select a leave type"),
    startDate: z.date(),
    endDate: z.date(),
    notes: z.string().max(500, "Notes cannot exceed 500 characters").optional(),
  })
  .refine(
    (data) => {
      return !isBefore(data.endDate, data.startDate);
    },
    {
      message: "End date must be after start date",
      path: ["endDate"],
    },
  );

type FormValues = z.infer<typeof timeOffSchema>;

const TimeOffSection: React.FC<TimeOffSectionProps> = ({
  timeOffRequests,
  leavePolicies,
  submissionId,
  isEditable,
  onUpdate,
}) => {
  const { toast } = useToast();
  const { filteredItems, showDeleted, setShowDeleted } =
    useFilteredRequests(timeOffRequests);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date | undefined;
  }>({
    from: new Date(),
    to: undefined,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(timeOffSchema),
    defaultValues: {
      leaveTypeId: "",
      startDate: new Date(),
      endDate: addDays(new Date(), 1),
      notes: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      // Convert the date to YYYY-MM-DD format before sending
      const formattedValues = {
        ...values,
        leaveTypeVersionId: values.leaveTypeId,
      };

      const response = await ApiService.request<TimeOffResponse>(
        EndpointKeys.CREATE_TIME_OFF,
        {
          method: "POST",
          urlParams: { submissionId },
          body: formattedValues,
        },
      );

      // Update parent state with new time off request
      const newTimeOff = response.data;
      onUpdate([...timeOffRequests, newTimeOff]);

      toast({
        title: "Success",
        description: "Time off request submitted successfully",
      });

      form.reset();
      setIsDialogOpen(false);
    } catch (err) {
      // Default error message
      let errorMessage = "Failed to submit time off request";
      
      console.error("Time off request error:", err);
      
      // Check for ApiError from our service
      if (err && typeof err === 'object' && 'name' in err && err.name === 'ApiError') {
        // It's our ApiError type
        const apiError = err as any;
        if (apiError.body && apiError.body.error) {
          // Just extract the plain error message string without the JSON structure
          errorMessage = apiError.body.error;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
        console.log("Using ApiError message:", errorMessage);
      } 
      // Fallback for other error types
      else if (err instanceof Error && err.message) {
        errorMessage = err.message;
        console.log("Using standard Error message:", errorMessage);
      }
      
      // Clean up any remaining JSON-like strings in the error message
      if (errorMessage.startsWith('{"error":')) {
        try {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || errorMessage;
        } catch (e) {
          // If parsing fails, keep the original message
        }
      }
      
      // Remove any quotes and ensure proper capitalization
      errorMessage = errorMessage.replace(/^["'](.*)["']$/, '$1');
      if (errorMessage.length > 0) {
        errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await ApiService.request(EndpointKeys.DELETE_TIME_OFF, {
        urlParams: { submissionId, id },
        method: "DELETE",
      });

      // Update parent state by filtering out deleted time off request
      const updatedTimeOff = timeOffRequests.filter(
        (request) => request.id !== id,
      );
      onUpdate(updatedTimeOff);

      toast({
        title: "Success",
        description: "Time off request deleted successfully",
      });
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "Failed to delete time off request",
        variant: "destructive",
      });
    }
  };

  const calculateDuration = (startDate: Date, endDate: Date) => {
    // Add 1 to make the range inclusive (count both start and end dates)
    const diffDays = differenceInDays(endDate, startDate) + 1;
    return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  };

  return (
    <div>
      {isEditable && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Time Off</DialogTitle>
              <DialogDescription>
                Submit a time off request for approval
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="leaveTypeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Leave Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select leave type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {leavePolicies.map((leavePolicy) => (
                            <SelectItem
                              key={leavePolicy.leaveTypeVersionId}
                              value={leavePolicy.leaveTypeVersionId.toString()}
                            >
                              {leavePolicy.policyDisplayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Start Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0 pointer-events-auto"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              onSelect={(date) => {
                                field.onChange(date);
                                if (date && form.getValues("endDate")) {
                                  if (
                                    isBefore(form.getValues("endDate"), date)
                                  ) {
                                    form.setValue("endDate", date);
                                  }
                                }
                              }}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>End Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0 pointer-events-auto"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              onSelect={field.onChange}
                              disabled={(date) => {
                                const startDate = form.getValues("startDate");
                                return startDate
                                  ? date < startDate
                                  : date < new Date();
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Add any additional notes (optional)"
                        />
                      </FormControl>
                      <FormDescription>
                        Optional notes for your time off request
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full">
                  Submit Request
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex justify-between items-center mb-4">
        {isEditable && (
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Request Time Off
          </Button>
        )}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-deleted"
            checked={showDeleted}
            onCheckedChange={setShowDeleted}
          />
          <Label htmlFor="show-deleted">Show deleted requests</Label>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Status</TableHead>
              {isEditable && (
                <TableHead className="w-[100px]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isEditable ? 6 : 5}
                  className="text-center text-muted-foreground"
                >
                  No time off requests
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.leave_type_name}</TableCell>
                  <TableCell>
                    {format(new Date(request.start_date), "PPP")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.end_date), "PPP")}
                  </TableCell>
                  <TableCell>
                    {calculateDuration(
                      new Date(request.start_date),
                      new Date(request.end_date),
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "rounded-full px-2 py-1 text-xs font-medium",
                        {
                          "bg-green-100 text-green-800":
                            request.status === "APPROVED",
                          "bg-yellow-100 text-yellow-800":
                            request.status === "PENDING",
                          "bg-red-100 text-red-800":
                            request.status === "REJECTED",
                        },
                      )}
                    >
                      {request.status}
                    </span>
                  </TableCell>
                  {isEditable && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(request.id)}
                        className="hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TimeOffSection;
